import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiMinus, FiArrowRight } = FiIcons;

const faqItems = [
  {
    q: 'What is HostFig?',
    a: 'HostFig is a digital solutions company that helps businesses establish and grow their online presence. We provide website development, web hosting, cloud hosting, domain registration, business email, HostFig Studio imagery, branding, SEO, website maintenance, and ongoing technical support—all under one trusted partner.'
  },
  {
    q: 'What services does HostFig offer?',
    a: 'We offer a wide range of digital services, including: Website Design & Development, Custom Web Applications, Managed Web Hosting, Cloud Hosting, Domain Registration, Professional Business Email, HostFig Studio Imagery, Branding & Logo Design, SEO Services, Website Maintenance, Website Migration, SSL Certificates, and DNS Management.'
  },
  {
    q: 'Can HostFig build a custom website for my business?',
    a: 'Yes. We specialize in creating custom websites tailored to your business goals. Whether you need a company website, online store, booking platform, portfolio, or a custom web application, our team can design and develop a solution that fits your needs.'
  },
  {
    q: 'Do you provide web hosting?',
    a: 'Yes. HostFig offers secure and reliable web hosting with fast performance, SSL certificates, backups, monitoring, and technical support to keep your website running smoothly.'
  },
  {
    q: 'Can I register a domain through HostFig?',
    a: 'Yes. We offer domain registration, domain transfers, DNS management, and domain renewal services, making it easy to manage your online identity in one place.'
  },
  {
    q: 'What is HostFig Studio?',
    a: 'HostFig Studio provides professional catalog-style images and model-like photography featuring your products. We deliver high-end, professional imagery designed to elevate your brand\'s visual identity and presentation without the overhead of traditional photoshoots.'
  },
  {
    q: 'Do you offer business email services?',
    a: 'Yes. We provide professional email hosting using your own domain name, along with email migration, security configuration (SPF, DKIM, DMARC), spam protection, and ongoing support.'
  },
  {
    q: 'Can you redesign my existing website?',
    a: 'Absolutely. We can modernize your existing website with a fresh design, improved performance, enhanced security, better SEO, and a mobile-friendly user experience.'
  },
  {
    q: 'Do you migrate websites from other hosting providers?',
    a: 'Yes. Our team can safely migrate your website, databases, emails, and domains from your current provider to HostFig with minimal downtime.'
  },
  {
    q: 'Do you provide ongoing website maintenance?',
    a: 'Yes. We offer maintenance plans that include software updates, security monitoring, backups, performance optimization, bug fixes, and technical support.'
  },
  {
    q: 'Can HostFig help improve my website\'s Google ranking?',
    a: 'Yes. Our SEO services include technical optimization, keyword strategy, on-page SEO, performance improvements, and analytics setup to help increase your online visibility.'
  },
  {
    q: 'What industries do you work with?',
    a: 'We work with businesses of all sizes across many industries, including retail, hospitality, healthcare, education, fitness, real estate, professional services, construction, e-commerce, and startups.'
  },
  {
    q: 'How long does it take to build a website?',
    a: 'Project timelines depend on the complexity of the website. Simple business websites may take a few weeks, while larger e-commerce or custom web applications may require more time. We\'ll provide an estimated timeline before the project begins.'
  },
  {
    q: 'Will my website work on mobile devices?',
    a: 'Yes. Every website we build is fully responsive and optimized to work seamlessly across desktops, tablets, and smartphones.'
  },
  {
    q: 'Can I update my website after it\'s launched?',
    a: 'Yes. Depending on the solution, you\'ll have access to update your content yourself, or we can manage updates for you through one of our maintenance plans.'
  },
  {
    q: 'Do you offer hosting for websites not built by HostFig?',
    a: 'Yes. You can host your existing website with HostFig even if it was developed by another company.'
  },
  {
    q: 'Is technical support included?',
    a: 'Yes. Our team is available to assist with hosting, websites, email services, and technical issues. The level of support depends on your selected service or maintenance plan.'
  },
  {
    q: 'How do I request a quotation?',
    a: 'Simply complete the enquiry form on our website with your project details. Our team will review your requirements and contact you with a tailored proposal.'
  },
  {
    q: 'Why should I choose HostFig?',
    a: 'HostFig combines multiple digital services under one trusted partner, saving you the hassle of working with different providers. We focus on delivering reliable technology, transparent communication, modern solutions, and long-term support to help your business grow.'
  },
  {
    q: 'How do I get started?',
    a: 'Getting started is easy. Contact us through our website, tell us about your project or business goals, and our team will guide you through the next steps—from planning and design to launch and ongoing support.'
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.05
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

const PrimaryButton = ({ children, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.button
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      className="relative inline-flex items-center justify-between rounded-full h-[56px] overflow-hidden min-w-[280px] p-[4px_4px_4px_18px] w-[300px]"
      style={{
        background: 'linear-gradient(145deg, #2563EB 4.96%, #3B82F6 92.76%)'
      }}
    >
      <motion.div
        initial={false}
        animate={{ scale: isHovered ? 15 : 1 }}
        transition={{ duration: 0.85, ease: [0.25, 1, 0.5, 1] }}
        className="absolute right-[4px] top-[6px] w-[44px] h-[44px] rounded-full bg-white origin-center"
      />
      <motion.span
        animate={{ color: isHovered ? '#2563EB' : '#ffffff' }}
        transition={{ duration: 0.5 }}
        className="relative z-10 text-[17px] font-semibold tracking-tight"
      >
        {children}
      </motion.span>
      <motion.div
        animate={{ backgroundColor: isHovered ? 'transparent' : '#ffffff' }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-11 h-11 rounded-full flex items-center justify-center shrink-0"
      >
        <SafeIcon
          icon={FiArrowRight}
          className="w-5 h-5 text-[#2563EB]"
          style={{ strokeWidth: 2.5 }}
        />
      </motion.div>
    </motion.button>
  );
};

const FAQ = () => {
  const [activeFaq, setActiveFaq] = useState(0);
  const navigate = useNavigate();

  return (
    <section id="faq" className="w-full bg-[#ffffff] flex justify-center select-none overflow-hidden font-sans border-t border-slate-100">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        variants={containerVariants}
        className="relative w-full max-w-[1440px] px-6 py-[80px] md:px-[48px] lg:px-[96px] bg-[#ffffff] flex flex-col items-center"
      >
        <div className="w-full max-w-[1248px] flex flex-col lg:grid lg:grid-cols-[506px_1fr] lg:gap-x-[48px] justify-between items-stretch">
          <motion.div variants={itemVariants} className="lg:sticky lg:top-32 self-start">
            <div className="inline-flex items-center gap-[10px] rounded-full px-[18px] py-[8px] bg-[#EFF6FF] border border-[#2563EB]/10">
              <span className="text-[14px] font-semibold text-[#2563EB] tracking-tight uppercase">FAQ</span>
            </div>
            <h2 className="mt-[24px] text-[28px] md:text-[38px] lg:text-[56px] font-bold text-[#0F172A] leading-tight tracking-tight">
              Frequently Asked Questions
            </h2>
            <p className="mt-[16px] text-[16px] md:text-[18px] leading-relaxed text-slate-600 max-w-[420px] mb-12">
              Got questions? Here are clear answers to help you understand our process, services and technology.
            </p>
            <PrimaryButton onClick={() => navigate('/contact')}>
              Have more questions?
            </PrimaryButton>
          </motion.div>

          <div className="mt-[40px] lg:mt-0 flex flex-col gap-[12px] w-full">
            {faqItems.map((item, index) => {
              const isOpen = activeFaq === index;
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  onClick={() => setActiveFaq(isOpen ? null : index)}
                  className="rounded-[12px] border transition-all duration-300 px-[24px] md:px-[32px] py-[20px] cursor-pointer"
                  style={{
                    backgroundColor: isOpen ? '#EFF6FF' : '#ffffff',
                    borderColor: isOpen ? 'rgba(37, 99, 235, 0.15)' : 'rgba(15, 23, 42, 0.04)'
                  }}
                >
                  <div className="flex items-center justify-between gap-4">
                    <h3 className="text-[17px] md:text-[19px] font-semibold text-[#0F172A]">
                      {item.q}
                    </h3>
                    <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0">
                      <SafeIcon
                        icon={isOpen ? FiMinus : FiPlus}
                        className="w-[20px] h-[20px] text-[#2563EB]"
                        style={{ strokeWidth: 2.5 }}
                      />
                    </div>
                  </div>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto", marginTop: 16 }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <p className="text-[16px] leading-relaxed text-slate-600 font-normal">
                          {item.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default FAQ;